import { Button } from '@design-system/button';
import { Typography } from '@design-system/typography';
import { useTranslations } from '@phx-husky/use-translations';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { useMemo } from 'react';
import {
  EventType,
  getEventTypeIconSrc,
  ICON_BY_TYPE,
  mapEventDetails,
} from '../../../templates/L2EventPage/utils';
import getLocalTimeData from '~/utils/getLocalTime';
import DetailGroup from '~/components/HCP/DetailGroup/DetailGroup';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useModal } from '~/context/ModalContext/ModalContext';
import EventModal from '~/components/Modals/EventModal';

import { EventSubscriptionSectionProps } from '../models';

import './event-subscription-section.scss';

export const EventSubscriptionSection = ({
  eventUids,
  onUnsubscribe,
  modalContent,
}: EventSubscriptionSectionProps) => {
  const { translate } = useTranslations();

  const data = useStaticQuery(graphql`
    query EventSubscriptionEvents {
      allContentstackL2EventPage {
        nodes {
          uid
          url
          locale
          seo {
            title
          }
          publish_details {
            time
          }
          event_timestamp {
            event_end_date
            event_start_date
          }
          event_data {
            event_type
          }
        }
      }
      allContentstackTranslations {
        nodes {
          event_page_translations {
            worshop
            time
            presenters
            online_event
            on_site
            offline_event
            date
            my_events
          }
        }
      }
    }
  `);
  const { openModal } = useModal();

  const timeZone = process.env.GATSBY_HCP_SITE_IANA_TIME_ZONE;

  const contentstackTranslations =
    data.allContentstackTranslations.nodes[0]?.event_page_translations;

  const t = mapContentstackTranslations(contentstackTranslations);

  const subscribedEvents = useMemo(
    () =>
      data.allContentstackL2EventPage.nodes.filter((event) =>
        eventUids.includes(event.uid)
      ),
    [data, eventUids]
  );

  const preparedEvents = useMemo(() => {
    return [...subscribedEvents]
      .sort((a, b) => {
        const dateA = new Date(a.event_timestamp?.event_start_date).getTime();
        const dateB = new Date(b.event_timestamp?.event_start_date).getTime();

        return dateA - dateB;
      })
      .map((event) => {
        const eventStartDate = getLocalTimeData(
          event.event_timestamp?.event_start_date,
          timeZone,
          event.locale
        );

        const eventEndDate = getLocalTimeData(
          event.event_timestamp?.event_end_date,
          timeZone,
          event.locale
        );

        const eventType = event.event_data?.event_type ?? EventType.ONLINE;

        const eventTypeIcon = (
          <img
            src={getEventTypeIconSrc(eventType)}
            alt={ICON_BY_TYPE[eventType]}
          />
        );

        const details = mapEventDetails(
          t,
          eventStartDate,
          eventEndDate,
          eventTypeIcon,
          eventType
        );

        return {
          ...event,
          details,
        };
      });
  }, [subscribedEvents, timeZone, t]);

  return (
    <div className="event-subscription-section">
      <Typography
        variant="body"
        tag="span"
        size="xl"
        weight="bold"
        className="event-subscription-section__title"
        content={translate('userEventSubsctiptions')}
      />

      {preparedEvents.length === 0 ? (
        <Typography
          variant="body"
          tag="div"
          size="m"
          weight="regular"
          className="event-subscription-section__noSubscriptions"
          content={
            translate('userEventNoSubscriptions') ||
            'You have no subscribed events.'
          }
        />
      ) : (
        <div className="event-subscription-section__list">
          {preparedEvents.map((event) => (
            <div key={event.uid} className="event-subscription-section__item">
              <Link to={event.url} className="event-subscription-section__link">
                {event.seo.title}
              </Link>
              <DetailGroup items={event.details} trailing={null} />
              <Button
                variant="secondary"
                className="event-subscription-section__unsubscribeButton"
                type="button"
                onClick={() =>
                  openModal(
                    <EventModal
                      onSubmit={() => onUnsubscribe(event.uid)}
                      {...modalContent}
                    />
                  )
                }
              >
                {translate('userEventUnsubscribeButton')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
